import React, { useState, useEffect, useMemo } from "react"
import { Search, MoreHorizontal, ArrowUpDown, CheckCircle2, Clock, RotateCcw, XCircle } from "lucide-react"
import { Card, CardContent } from "../../../components/ui/card"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../../components/ui/table"
import Input from "../../../components/ui/Input"
import { listOrgOrders, formatOrderDisplayId } from "../../../utils/orderHelpers"
import CustomerOverviewSection from "./CustomerOverviewSection"

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value || 0)

const formatDate = (value) => {
  const ms = value?.toMillis ? value.toMillis() : new Date(value).getTime()
  if (!ms) return "—"
  return new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}

const statusConfig = {
  paid: { label: "Success", icon: CheckCircle2, className: "bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-600" },
  pending: { label: "Pending", icon: Clock, className: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-600" },
  refunded: { label: "Refunded", icon: RotateCcw, className: "bg-muted text-muted-foreground" },
  failed: { label: "Failed", icon: XCircle, className: "bg-red-100 text-destructive dark:bg-red-950" },
}

const StatusPill = ({ status }) => {
  const config = statusConfig[status] || statusConfig.pending
  const Icon = config.icon
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${config.className}`}>
      <Icon className="size-3" />
      {config.label}
    </span>
  )
}

const RowActionsMenu = ({ onViewDetails }) => {
  const [open, setOpen] = useState(false)
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
      >
        <MoreHorizontal className="size-4" />
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1 w-40 rounded-md border border-border bg-card shadow-md py-1">
          <button
            onClick={onViewDetails}
            className="block w-full px-3 py-1.5 text-left text-sm text-foreground hover:bg-muted"
          >
            View Details
          </button>
        </div>
      )}
    </div>
  )
}

const SortableHeader = ({ label, sortKey, currentSort, onSort }) => (
  <button onClick={() => onSort(sortKey)} className="flex items-center gap-1 hover:text-foreground">
    {label}
    <ArrowUpDown className={`size-3 ${currentSort?.key === sortKey ? "text-foreground" : "text-muted-foreground/50"}`} />
  </button>
)

const CommerceOrdersPanel = ({ organization }) => {
  const orgId = organization?.orgId
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [sort, setSort] = useState({ key: "createdAt", dir: "desc" })

  useEffect(() => {
    if (!orgId) return
    setLoading(true)
    listOrgOrders(orgId).then(({ orders: data }) => {
      setOrders(data || [])
      setLoading(false)
    })
  }, [orgId])

  const handleSort = (key) => {
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }))
  }

  const toMs = (value) => (value?.toMillis ? value.toMillis() : new Date(value).getTime() || 0)

  const filteredAndSorted = useMemo(() => {
    const q = search.trim().toLowerCase()
    let result = orders
    if (q) {
      result = orders?.filter(
        (o) =>
          o?.customerName?.toLowerCase()?.includes(q) ||
          o?.customerEmail?.toLowerCase()?.includes(q) ||
          o?.productTitle?.toLowerCase()?.includes(q) ||
          formatOrderDisplayId(o?.orderId)?.toLowerCase()?.includes(q)
      )
    }
    const sorted = [...(result || [])]?.sort((a, b) => {
      let cmp = 0
      if (sort.key === "customer") cmp = (a?.customerName || "")?.localeCompare(b?.customerName || "")
      else if (sort.key === "product") cmp = (a?.productTitle || "")?.localeCompare(b?.productTitle || "")
      else if (sort.key === "amount") cmp = (a?.amount || 0) - (b?.amount || 0)
      else if (sort.key === "status") cmp = (a?.status || "")?.localeCompare(b?.status || "")
      else cmp = toMs(a?.paidAt || a?.createdAt) - toMs(b?.paidAt || b?.createdAt)
      return sort.dir === "asc" ? cmp : -cmp
    })
    return sorted
  }, [orders, search, sort])

  const allSelected = filteredAndSorted?.length > 0 && selectedIds.size === filteredAndSorted?.length

  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set())
    else setSelectedIds(new Set(filteredAndSorted?.map((o) => o.orderId)))
  }

  const toggleOne = (orderId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(orderId)) next.delete(orderId)
      else next.add(orderId)
      return next
    })
  }

  return (
    <div>
      <CustomerOverviewSection orgId={orgId} dateRange="30d" showReportLink={false} />

      <Card className="shadow-none">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-base font-semibold text-foreground">Recent Orders</h2>
          <div className="relative w-64">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
            <Input
              placeholder="Search orders..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9 text-sm"
            />
          </div>
        </div>

        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-3">
              {Array.from({ length: 6 })?.map((_, i) => <div key={i} className="h-10 rounded-lg bg-muted animate-pulse" />)}
            </div>
          ) : !filteredAndSorted?.length ? (
            <div className="p-8 text-center text-muted-foreground">
              {orders?.length ? "No orders match your search." : "No orders yet. Orders will show up here as customers purchase your products."}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} className="size-4 rounded border-input" />
                  </TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead>
                    <SortableHeader label="Customer" sortKey="customer" currentSort={sort} onSort={handleSort} />
                  </TableHead>
                  <TableHead>
                    <SortableHeader label="Product" sortKey="product" currentSort={sort} onSort={handleSort} />
                  </TableHead>
                  <TableHead>
                    <SortableHeader label="Amount" sortKey="amount" currentSort={sort} onSort={handleSort} />
                  </TableHead>
                  <TableHead>
                    <SortableHeader label="Status" sortKey="status" currentSort={sort} onSort={handleSort} />
                  </TableHead>
                  <TableHead>
                    <SortableHeader label="Date" sortKey="date" currentSort={sort} onSort={handleSort} />
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSorted.map((order) => (
                  <TableRow key={order.orderId}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(order.orderId)}
                        onChange={() => toggleOne(order.orderId)}
                        className="size-4 rounded border-input"
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{formatOrderDisplayId(order.orderId)}</TableCell>
                    <TableCell>
                      <div className="font-medium text-foreground">{order.customerName || "—"}</div>
                      <div className="text-xs text-muted-foreground">{order.customerEmail}</div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{order.productTitle}</TableCell>
                    <TableCell className="font-medium text-foreground">{formatCurrency(order.amount)}</TableCell>
                    <TableCell>
                      <StatusPill status={order.status} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">{formatDate(order.paidAt || order.createdAt)}</TableCell>
                    <TableCell className="text-right">
                      <RowActionsMenu onViewDetails={() => {}} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default CommerceOrdersPanel